import {
  AssistantLayout,
  useAssistantPanelState,
} from '@/features/assistant/AssistantLayout.js';
import type { PanelSend } from '@/features/assistant/usePanelSend.js';
import type { QualityRunMenuAction } from './QualityRunActionsMenu.js';
import { sendRunAction } from './run-actions/sendRunAction.js';
import { useCallback } from 'react';
import { flushSync } from 'react-dom';
import {
  GenerationResultFeedback,
  retainedGenerationWarning,
} from './GenerationResultFeedback.js';
import { useSyncExternalStore } from 'react';
import { getState, subscribe } from '@/store/identity-store.js';
import { RelaunchRunDialog } from './RelaunchRunDialog.js';
import { QueueWaitNotice } from './QueueWaitNotice.js';
import { OutcomeActivity } from './OutcomeActivity.js';
import {
  OutcomeCorrectionDialog,
  type CorrectableOutcome,
} from './OutcomeCorrectionDialog.js';
import type { RelaunchRunBody } from '@/lib/api/quality.js';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  AlertTriangleIcon,
  ArrowLeftIcon,
  BugIcon,
  ExternalLinkIcon,
  InfoIcon,
  LayersIcon,
  ListChecksIcon,
  PencilIcon,
  RotateCcwIcon,
  RotateCwIcon,
  Trash2Icon,
  VideoIcon,
  XCircleIcon,
} from 'lucide-react';
import {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react';

import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { AppDialog } from '@/components/ui/app-dialog.js';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs.js';
import { ConfirmDeleteDialog } from '@/features/automations/ConfirmDeleteDialog.js';
import { useQualityRunStream } from '@/integration/runtime/quality-run-stream.js';
import {
  artifactUrl,
  QualityApiError,
  reportBundleQueryOptions,
  runDetailQueryOptions,
  useCancelRun,
  useCancelCase,
  useDeleteRun,
  useRerunRun,
  useRerunCurrentRun,
  useRestartFailed,
} from '@/lib/api/quality.js';
import {
  runName,
  runOutputSource,
  type AttachmentIssue,
  type CaseAttempt,
  type Run,
  type RunDetail,
  type RunStage,
  type TestResult,
  type SourceRef,
  type TestSource,
} from '@/lib/api/quality-types.js';
import { notify } from '@/lib/notify.js';
import { cn } from '@/lib/utils.js';

import { RunActions } from './run-actions/RunActions.js';
import {
  validateAgentActionHandoff,
  type AgentActionHandoffResult,
} from './run-actions/agentActionHandoff.js';
import { ExplorationReport } from './ExplorationReport.js';
import { ExploratoryArtifactPanel } from './ExploratoryArtifactPanel.js';
import { GeneratedCasesResult } from './GeneratedCasesResult.js';
import { GenerationActivity } from './GenerationActivity.js';
import { GenerationLog } from './GenerationLog.js';
import { GenerationTrace } from './GenerationTrace.js';
import { InputRequiredPanel } from './InputRequiredPanel.js';
import {
  PlayerDock,
  PLAYER_DOCK_ID,
  type PlayerDockLoadRequest,
  type PlayerDockSeekRequest,
} from './player/PlayerDock.js';
import {
  collectRecordings,
  resolveChapterScope,
  resolveDeepLinkRecording,
  resolveRecording,
  wholeRecordingSelection,
  type Recording,
  type StepSeekHandler,
} from './player/recordings.js';
import { TimeSinkStrip } from './player/TimeSinkStrip.js';
import { RefinePanel } from './RefinePanel.js';
import { ReviewProposedCases } from './ReviewProposedCases.js';
import { WorkspaceKnowledgeRunNotice } from './WorkspaceKnowledgeRunNotice.js';
import { ExecutionEstimate, formatRecordedUsd } from './ExecutionEstimate.js';
import { AnswerJudgementSummaryLine } from './reports/AnswerJudgement.js';
import { AnswerJudgeReport } from './reports/AnswerJudgeReport.js';
import { CasesReport, revealFirstReviewCase } from './reports/CasesReport.js';
import { ErrorDigestReport } from './reports/ErrorDigestReport.js';
import { VideoReport } from './reports/VideoReport.js';
import {
  badgeStatus,
  CancelReasonTooltip,
  caseReasonText,
  deviationCount,
  formatDate,
  formatDuration,
  isTerminalRun,
  normalizeRunSummary,
  platformComponentIcon,
  PlatformIcon,
  ReapedReasonBadge,
  runReapedReasonText,
  StatusBadge,
  OutcomeResultsSummary,
  OutcomeRunBadge,
} from './quality-presentation.js';
import {
  outcomeProjection,
  restartableCases,
  runLifecycleStatus,
} from './quality-outcome.js';

/**
 * The suite a run was launched from, when it maps to a browsable suite-detail page: a suite stored
 * in Quality (from its canonical `source_ref`), a TestRail run (keyed on its run id), a Zephyr run
 * launched from a cycle (keyed on the cycle key), or an Azure run (keyed on its Test Plan suite id).
 * Folder-based Zephyr runs and inline/mock runs have no such page, so they stay a plain badge.
 * Carrying `source` lets the detail page pick the right provider — the id alone is ambiguous
 * across sources.
 */
export function runSuiteLink(
  run: Run,
): { id: string; source: TestSource; sourceRef?: SourceRef } | null {
  if (run.source_ref?.provider === 'own-tms') {
    /** The run is cast, not validated, so only an addressable suite reference becomes a link. */
    const coordinates = run.source_ref.coordinates as
      { kind?: string; suiteId?: string } | null | undefined;
    return coordinates?.kind === 'suite' && coordinates.suiteId
      ? {
          id: coordinates.suiteId,
          source: 'own-tms',
          sourceRef: run.source_ref,
        }
      : null;
  }
  if (run.testrail_run_id)
    return { id: run.testrail_run_id, source: 'testrail' };
  if (run.source === 'zephyr' && run.zephyr_cycle_key)
    return { id: run.zephyr_cycle_key, source: 'zephyr' };
  if (run.source === 'azure' && run.azure_suite_id)
    return { id: run.azure_suite_id, source: 'azure' };
  return null;
}

/** Whether "Rerun current source" can re-read this run's source; a stored suite needs its reference. */
export function currentSourceRerunAvailableFor(run: Run): boolean {
  if (run.source === 'own-tms') return Boolean(run.source_ref);
  if (!['testrail', 'azure', 'zephyr'].includes(run.source ?? '')) {
    return false;
  }
  if (run.source === 'testrail') {
    return Boolean(run.testrail_run_id || run.testrail_suite_id);
  }
  if (run.source === 'azure') {
    return Boolean(
      run.azure_project && run.azure_plan_id && run.azure_suite_id,
    );
  }
  return Boolean(run.zephyr_cycle_key || run.zephyr_folder_id);
}

function currentRerunRequestKey(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return `quality-rerun-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * "Variable overrides: USER=qa2, …" for a run launched with overrides (MEDA-865), or null. The
 * field is cast, not validated, so anything that is not a map of strings reads as no overrides.
 */
function variableOverridesText(overrides: unknown): string | null {
  if (!overrides || typeof overrides !== 'object' || Array.isArray(overrides))
    return null;
  const pairs = Object.entries(overrides).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string',
  );
  if (pairs.length === 0) return null;
  return `Variable overrides: ${pairs.map(([key, value]) => `${key}=${value}`).join(', ')}`;
}

const QualityRunAssistant = lazy(async () => ({
  default: (await import('./QualityRunAssistant.js')).QualityRunAssistant,
}));

export function QualityRunDetail({
  runId,
  caseId,
  rec,
  t,
  agentHandoff = { kind: 'none' },
}: {
  runId: string;
  /**
   * Deep-link search params (S11, AC 26/27), read by the route from the URL and passed down as
   * plain props so this component stays renderable in tests without a router. `caseId` aliases the
   * URL's reserved `case` key (see `quality_.$id.tsx`). All three are read only once, at the moment
   * data becomes available to resolve them (`initialDeepLinkRef` below) — after that, this
   * component's own explicit binds are what drive the URL, not the other way around.
   */
  caseId?: string;
  rec?: string;
  t?: number;
  agentHandoff?: AgentActionHandoffResult;
}): JSX.Element {
  const layoutRef = useRef<HTMLDivElement>(null);
  const panel = useAssistantPanelState(
    {
      width: 'quality-assistant-width',
      collapsed: 'quality-assistant-collapsed',
    },
    true,
  );
  const [feedbackContainer, setFeedbackContainer] =
    useState<HTMLDivElement | null>(null);
  const assistantSend = useRef<PanelSend | null>(null);
  const [assistantReady, setAssistantReady] = useState(false);
  const onSendReady = useCallback((send: PanelSend | null) => {
    assistantSend.current = send;
    setAssistantReady(send !== null);
  }, []);
  const guardAction = (blocked: boolean, action: () => void): void => {
    if (blocked)
      notify.warning(
        'Wait for the current action and report revision to finish, then try again.',
      );
    else action();
  };
  const remove = useDeleteRun(runId);
  const detailQuery = useQuery({
    ...runDetailQueryOptions(runId, {}, { pollWhileQueued: true }),
    enabled: typeof runId === 'string' && runId.length > 0 && !remove.isSuccess,
  });
  const detail = useMemo(() => {
    const value = detailQuery.data;
    if (!value) return undefined;
    if (Array.isArray(value.stages) && Array.isArray(value.results))
      return value;
    return {
      ...value,
      stages: Array.isArray(value.stages) ? value.stages : [],
      results: Array.isArray(value.results) ? value.results : [],
    };
  }, [detailQuery.data]);
  const projection = detail
    ? outcomeProjection(detail.run, detail.summary, detail.results)
    : null;
  const lifecycleStatus =
    projection?.kind === 'canonical'
      ? projection.executionStatus
      : projection?.kind === 'legacy'
        ? projection.status
        : null;
  const terminal = lifecycleStatus ? isTerminalRun(lifecycleStatus) : false;
  /** Whether the run is over, read from the run row itself rather than from `projection`. The
   *  projection answers "are this run's counters coherent enough to drive the outcome UI" and goes
   *  `unsupported` when they are not — which would leave a finished run's recordings still claiming
   *  they are being recorded. Nothing about a recording depends on the counters. */
  const runIsOver = detail
    ? (() => {
        const status = runLifecycleStatus(detail.run);
        return status ? isTerminalRun(status) : false;
      })()
    : false;
  const explorationResult = detail?.results.find(
    (result) => result.case_id === 'exploration',
  );
  const suiteRef = detail ? runSuiteLink(detail.run) : null;
  const currentSourceRerunAvailable = detail
    ? currentSourceRerunAvailableFor(detail.run)
    : false;

  // Canonical terminal runs stay subscribed because manual corrections can advance their revision.
  useQualityRunStream(
    runId,
    projection?.kind === 'canonical' ||
      (projection?.kind !== 'unsupported' && !terminal),
  );

  // Load the report bundle as soon as the run exists (not only once finalized): the service assembles
  // it live from per-case fragments, and the SSE `result.updated` invalidation refetches it as cases
  // finish — so rich per-case data (steps, errors, video) appears during the run.
  const reportQuery = useQuery(
    reportBundleQueryOptions(
      runId,
      Boolean(detail) && !remove.isSuccess,
      projection?.kind === 'canonical' ? projection.reportRevision : undefined,
    ),
  );
  const bundle = reportQuery.data;
  const reportTotals = bundle?.summary?.totals;
  const judgeCost = formatRecordedUsd(
    detail?.answer_judgement_summary?.judge_cost_usd ?? null,
  );
  const reportCases = Array.isArray(bundle?.cases?.cases)
    ? bundle.cases.cases
    : undefined;
  const reportSessions = Array.isArray(bundle?.summary?.sessions)
    ? bundle.summary.sessions
    : undefined;
  const executionEstimate =
    detail?.execution_estimate ??
    detail?.run.execution_estimate ??
    bundle?.summary?.execution_estimate;
  const reportChanged =
    reportQuery.isFetching ||
    reportQuery.isError ||
    bundle?.coherence === 'changed' ||
    (projection?.kind === 'canonical' &&
      (bundle?.coherence !== 'coherent' ||
        bundle.reportRevision !== projection.reportRevision));

  const navigate = useNavigate();
  const cancel = useCancelRun(runId);
  const cancelCase = useCancelCase(runId);
  const { identity } = useSyncExternalStore(subscribe, getState, getState);
  const [relaunchMode, setRelaunchMode] = useState<'restart' | 'rerun' | null>(
    null,
  );
  const restart = useRestartFailed(runId);
  const rerun = useRerunRun(runId);
  const rerunCurrent = useRerunCurrentRun(runId);
  const [currentRerunOpen, setCurrentRerunOpen] = useState(false);
  const webNetworkCaptureAvailable = detail?.run.components?.length
    ? detail.run.components.includes('web')
    : detail?.run.platform === 'web';
  const [correction, setCorrection] = useState<{
    result: TestResult;
    initialOutcome?: CorrectableOutcome;
  } | null>(null);
  const handoffStartedRef = useRef(false);
  const [handoffRetry, setHandoffRetry] = useState(0);
  const [handoffError, setHandoffError] = useState<string | null>(null);
  const handoffIdentity =
    agentHandoff.kind === 'valid'
      ? `${agentHandoff.value.action}:${agentHandoff.value.reportRevision}:${JSON.stringify(agentHandoff.value.caseIds)}:${JSON.stringify(agentHandoff.value.outcomes)}`
      : agentHandoff.kind;

  useEffect(() => {
    handoffStartedRef.current = false;
    setHandoffError(null);
  }, [runId, handoffIdentity, handoffRetry]);

  useEffect(() => {
    if (
      !assistantReady ||
      agentHandoff.kind !== 'valid' ||
      handoffStartedRef.current ||
      !detail ||
      reportQuery.isLoading ||
      reportQuery.isFetching
    )
      return;
    if (reportQuery.isError) {
      setHandoffError(
        'The current report could not be loaded for this Agent action.',
      );
      return;
    }
    const error = validateAgentActionHandoff(
      agentHandoff.value,
      detail,
      bundle,
    );
    if (error) {
      setHandoffError(error);
      return;
    }
    handoffStartedRef.current = true;
    const send = assistantSend.current;
    if (!send) return;
    void sendRunAction(runId, agentHandoff.value.action, send, {
      caseIds: agentHandoff.value.caseIds,
      outcomes: agentHandoff.value.outcomes,
      reportRevision: agentHandoff.value.reportRevision,
    }).catch((error: unknown) => {
      setHandoffError(
        error instanceof Error
          ? error.message
          : 'The editable Agent draft could not be prepared.',
      );
    });
  }, [
    agentHandoff,
    assistantReady,
    runId,
    handoffRetry,
    bundle,
    detail,
    navigate,
    reportQuery.isError,
    reportQuery.isFetching,
    reportQuery.isLoading,
  ]);

  const [tab, setTab] = useState('cases');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const restartCandidates =
    detail && projection?.kind === 'canonical'
      ? restartableCases(detail)
      : undefined;
  const currentCorrectionResult = correction
    ? (detail?.results.find(
        (result) => result.case_id === correction.result.case_id,
      ) ?? correction.result)
    : null;

  /** The single docked player's state (S5): the bound recording's key, the pending seek command, an
   *  explicit load command, and whether the floating panel is showing. The panel is how the player
   *  appears everywhere except the Video tab, where it becomes that tab's own wide column instead —
   *  `drawerOpen` governs the panel only. `VideoReport` (S8) is a stateless selector with no player
   *  of its own, so nothing needs to stay mounted across a tab switch to preserve a loaded blob —
   *  that responsibility lives entirely in the dock. */
  const [boundRecordingKey, setBoundRecordingKey] = useState<string | null>(
    null,
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dockSeek, setDockSeek] = useState<PlayerDockSeekRequest | null>(null);
  const [dockLoadRequest, setDockLoadRequest] =
    useState<PlayerDockLoadRequest | null>(null);
  /** Which case (and, for a re-queued case, which attempt) the dock is bound to (S9) — drives the
   *  chapters list, which is scoped to that one case's steps and never the whole run/session. Both
   *  `handleSeek` (Cases tab) and `handleVideoSelect` (Video tab) set this from the click itself,
   *  never from the bound recording — a mobile session can cover more than one case, so which case
   *  is meant cannot be derived from the recording alone. */
  /** Whether what is bound got there by a real user action (`bindRecording`) rather than by a deep
   *  link's restore (`armRecording`). The restore opens the panel — the link points at a moment, so
   *  the player has to be visible — but "visible" is not consent to download: the poster is an
   *  artifact fetch and waits for the same gesture the recording itself waits for. */
  const [boundByGesture, setBoundByGesture] = useState(false);
  const [boundCaseId, setBoundCaseId] = useState<string | null>(null);
  const [boundAttemptNumber, setBoundAttemptNumber] = useState<number | null>(
    null,
  );
  const dockSeekNonce = useRef(0);
  /** A deep link's target case (S11, AC 26/27) — expands that one row in the Cases tab on restore.
   *  Kept separate from `boundCaseId` (which any later click also drives): folding the two together
   *  would force-expand a case merely because the user selected its recording from the Video tab,
   *  which is outside this step. */
  const [restoredExpandCaseId, setRestoredExpandCaseId] = useState<
    string | null
  >(null);
  /** The deep-link props as they stood the moment this run's page was entered — re-captured
   *  whenever `runId` changes (below), never read live afterward, so this component's own writes
   *  to the URL (via `syncDeepLinkUrl`) never feed back into a second restore. */
  const initialDeepLinkRef = useRef({ caseId, rec, t });
  /** Guards the restore effect to at most one attempt per `runId` — set the moment it decides
   *  there is nothing to restore, or the moment it successfully resolves a recording, whichever
   *  comes first. */
  const deepLinkAppliedRef = useRef(false);
  useEffect(() => {
    setBoundRecordingKey(null);
    setDrawerOpen(false);
    setDockSeek(null);
    setDockLoadRequest(null);
    setBoundByGesture(false);
    setBoundCaseId(null);
    setBoundAttemptNumber(null);
    setRestoredExpandCaseId(null);
    initialDeepLinkRef.current = { caseId, rec, t };
    deepLinkAppliedRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberately re-snapshotting caseId/rec/t only when runId changes, never on their own updates (see initialDeepLinkRef above)
  }, [runId]);
  const recordings = useMemo(
    () =>
      collectRecordings({
        runId,
        cases: reportCases,
        sessions: reportSessions,
        runIsTerminal: runIsOver,
      }),
    [runId, reportCases, reportSessions, runIsOver],
  );
  const boundRecording =
    recordings.find((r) => r.key === boundRecordingKey) ?? null;
  /** Case-scoped chapters (AC 18): whichever case/attempt the last bind named, resolved against
   *  whatever recording ended up bound (see `resolveChapterScope`). */
  const { steps: boundCaseSteps, clipWindow: boundClipWindow } =
    resolveChapterScope({
      cases: reportCases,
      boundCaseId,
      boundAttemptNumber,
      boundRecording,
    });
  /** A frame from the bound case, shown behind the unloaded player so it reads as "press play"
   *  rather than as a broken black rectangle. Deliberately a screenshot the report ALREADY carries
   *  (and the cases table already fetches) — an unloaded player must not pull anything new. */
  const boundPosterUrl = useMemo(() => {
    for (const step of boundCaseSteps) {
      const shot = (
        Array.isArray(step.attachments) ? step.attachments : []
      ).find((attachment) => attachment.type === 'image');
      if (shot) return artifactUrl(runId, shot.key);
    }
    return null;
  }, [boundCaseSteps, runId]);

  const onCancel = (): void => {
    cancel.mutate(undefined, {
      onSuccess: () => notify.success('Run cancelled.'),
      onError: (err) => notify.error(reasonOf(err, 'Failed to cancel run')),
    });
  };

  const onDelete = (): void => {
    remove.mutate(undefined, {
      onSuccess: () => {
        setConfirmDelete(false);
        notify.success('Run deleted.');
        void navigate({
          to: '/quality',
          search: {
            tab: 'runs',
            projectId: detail?.run.project_id ?? undefined,
          },
        });
      },
      onError: (err) => notify.error(reasonOf(err, 'Failed to delete run')),
    });
  };

  const onRestart = (settings?: RelaunchRunBody): void => {
    if (reportChanged) {
      notify.warning(
        'The report changed. Wait for the current revision before restarting cases.',
      );
      return;
    }
    restart.mutate(settings, {
      onSuccess: (created) => {
        setRelaunchMode(null);
        notify.success('Restarting failed cases.');
        void navigate({ to: '/quality/$id', params: { id: created.id } });
      },
      onError: (err) => notify.error(reasonOf(err, 'Failed to restart run')),
    });
  };

  // Re-run every case again — this mints a NEW run, so jump to its detail page (like a fresh launch).
  const onRerun = (settings?: RelaunchRunBody): void => {
    if (reportChanged) {
      notify.warning(
        'The report changed. Wait for the current revision before re-running cases.',
      );
      return;
    }
    rerun.mutate(settings, {
      onSuccess: (created) => {
        setRelaunchMode(null);
        notify.success('Re-running all cases.');
        void navigate({ to: '/quality/$id', params: { id: created.id } });
      },
      onError: (err) => notify.error(reasonOf(err, 'Failed to re-run')),
    });
  };

  const openRelaunch = (mode: 'restart' | 'rerun'): void => {
    if (reportChanged) {
      notify.warning(
        `The report changed. Wait for the current revision before ${mode === 'restart' ? 'restarting cases' : 're-running cases'}.`,
      );
      return;
    }
    setRelaunchMode(mode);
  };

  // Video sources: web → per-case videos; mobile → per-session BrowserStack videos.
  const platform = bundle?.summary?.platform ?? detail?.run.platform;
  /** The gate is exactly "does the Video tab have a row to show", so it reads `recordings` — the
   *  same list `VideoReport` renders — instead of re-deriving the answer from the bundle. The
   *  re-derivation it replaces enumerated web videos by hand and then asked `sessions.some((s) =>
   *  s.video || s.status === 'pending')`, which left a closed session with no recording out of the
   *  gate and the tab disabled with nothing to explain it. A recording with no source still counts:
   *  it binds and shows the dock's own frame (AC 12) rather than hiding the affordance. */
  const hasVideo = recordings.length > 0;
  /** Whether the page splits into "player column + case switcher" — the Video tab only, and never
   *  for an exploratory run, which renders its own player inside `ExplorationReport`. Everywhere
   *  else the player is the floating panel and the report keeps the full width. */
  const videoTabLayout =
    hasVideo && tab === 'video' && detail?.run.run_kind !== 'exploratory';

  /**
   * A step's "jump to video" resolves via `resolveRecording` (S2): the component the step ran on
   * ('web'/'mobile'), falling back to the run's platform when the step predates per-step
   * attribution, and, for a re-queued case, the specific attempt it belongs to (so an earlier
   * attempt's step seeks its OWN recording rather than the case-level one). `attempt` is passed by
   * the per-attempt block; flat (single-attempt / schema ≤2) rows omit it → case-level video.
   *
   * One click binds the dock, opens it, loads it, and seeks — never a tab switch (AC 8) and never a
   * second "Load recording" click (the two-click gesture this replaces). A recording that exists but
   * isn't ready yet (mobile session still open, web video not yet uploaded) is bound too: the dock
   * shows its own pending frame instead of a silent no-op or a toast (AC 12).
   *
   * `resolveRecording` can still return null — a case with no session and no video for this
   * component. A per-step reachability check (hiding the timecode chip itself) would need a
   * predicate threaded through every step-rendering layer down to `StepRow` (`CasesReport` →
   * `CaseRow`/`AttemptBlock`/`EarlierSteps`/`FailureFirstBody`, plus `TimeSinkStrip`) for a case this
   * rare; a toast is the honest, surgical alternative the spec explicitly allows.
   */
  const handleSeek: StepSeekHandler = (
    caseId,
    ms,
    component,
    attempt,
    videoKey,
  ) => {
    const recording = resolveRecording({
      recordings,
      cases: reportCases ?? [],
      caseId,
      component,
      attempt,
      platform,
      videoKey,
    });
    if (!recording) {
      notify.info('No recording is available for this moment.');
      return;
    }
    bindRecording(recording, ms);
    /** The chapters list (S9) is scoped to exactly this case (and this attempt, for a re-queued
     *  case) — never the whole session. */
    setBoundCaseId(caseId);
    setBoundAttemptNumber(attempt?.attempt ?? null);
    syncDeepLinkUrl(caseId, recording.key, ms);
  };

  /** Binds the dock to `recording`, opens it, and sends the one-click load + (optional) seek
   *  commands — shared by `handleSeek` (Cases tab, S7) and the Video tab's selector (S8). `ms: null`
   *  means "just load, don't seek" (a web recording, or a mobile recording with no clip window for
   *  this case — Branch B — starts from the top of the session) — `dockSeek` is explicitly cleared
   *  in that case, or a seek armed by a PREVIOUS bind would survive this one and misapply against
   *  whatever loads next (`PlayerDock`'s own `seek.key` check guards the rest: a leftover seek can
   *  only ever replay against a recording sharing its exact key). */
  const bindRecording = (recording: Recording, ms: number | null): void => {
    dockSeekNonce.current += 1;
    const nonce = dockSeekNonce.current;
    setBoundRecordingKey(recording.key);
    setDockLoadRequest({ key: recording.key, nonce });
    setDockSeek(ms !== null ? { key: recording.key, ms, nonce } : null);
    setDrawerOpen(true);
    setBoundByGesture(true);
  };

  /** Restores a deep link's target recording (S11, AC 27): binds it and arms the seek exactly like
   *  `bindRecording`, but sends no load request — the dock shows the pending/load frame at the
   *  target timecode and fetches nothing until the user's own gesture (a "Load recording" click, or
   *  any of the explicit actions below). Never called from a real click; only from the restore
   *  effect further down. Clears a stale `dockSeek` the same way `bindRecording` does — see that
   *  function's comment. */
  const armRecording = (recording: Recording, ms: number | null): void => {
    dockSeekNonce.current += 1;
    const nonce = dockSeekNonce.current;
    setBoundRecordingKey(recording.key);
    setDockSeek(ms !== null ? { key: recording.key, ms, nonce } : null);
    setDrawerOpen(true);
    /** A restore is not a gesture, even though it opens the panel: nothing this path shows may pull
     *  an artifact, and that includes the poster. */
    setBoundByGesture(false);
  };

  /** Writes the deep-link search params (S11, AC 26) after an explicit bind — a timecode click, a
   *  time-sink click, or the Video tab's selector, the three actions that funnel through
   *  `bindRecording`. Always `replace: true` so browsing between moments never floods history; never
   *  called from `timeupdate` or from the restore path itself.
   *
   * `resetScroll: false` is the actual AC 8 fix: the router's `scrollRestoration` (`router.tsx`)
   * defaults every `navigate` to `resetScroll: true`, and `replace()` mints a fresh history key each
   * call — so without this, every timecode/selector/time-sink click jumped the page to the top.
   * `viewTransition: false` too: the router's `defaultViewTransition: true` would otherwise run a
   * full-document cross-fade over a playing video for what is, from the user's point of view, an
   * in-place param sync rather than a real navigation. */
  const syncDeepLinkUrl = (
    forCaseId: string | null,
    recordingKey: string,
    ms: number | null,
  ): void => {
    void navigate({
      to: '/quality/$id',
      params: { id: runId },
      search: (prev) => ({
        ...prev,
        case: forCaseId ?? undefined,
        rec: recordingKey,
        t: ms ?? undefined,
      }),
      replace: true,
      resetScroll: false,
      viewTransition: false,
    });
  };

  /** The Video tab's selector (S8): selecting a recording or, within a shared mobile session, a
   *  specific case, binds the dock and seeks to that case's `clip_start_ms` when one exists. `caseId`
   *  scopes the chapters list (S9) the same way a Cases-tab timecode click does — `VideoReport` only
   *  supplies one when the row is unambiguous (a web recording, a single-case mobile session, or one
   *  case of an expanded multi-case session); an unattributed session (AC 17) has none, so chapters
   *  stay empty rather than guessing. `attemptNumber` rides on the recording itself: a web recording
   *  scoped to one attempt of a re-queued case carries it, so its chapters bind that attempt's own
   *  steps instead of the case's final ones. */
  const handleVideoSelect = (
    recording: Recording,
    seekMs: number | null,
    caseId?: string,
  ): void => {
    bindRecording(recording, seekMs);
    setBoundCaseId(caseId ?? null);
    setBoundAttemptNumber(recording.attemptNumber ?? null);
    syncDeepLinkUrl(caseId ?? null, recording.key, seekMs);
  };

  /** The "Show video" control (AC 7): the dock's own close button is otherwise a dead end on the
   *  Cases tab — with no timecode at hand, the user has no other way back to a recording they already
   *  closed. Reopening a recording that's already bound only flips `drawerOpen`, so the dock's own
   *  cached blob (if any) is reused as-is, never re-fetched; with nothing bound yet, this binds the
   *  run's first recording exactly like a Video-tab selection would — via `wholeRecordingSelection`,
   *  the same derivation the Video tab's own row uses, so a single-case mobile session opens at that
   *  case's clip start from either entry point, not at 0. */
  /** Every way the user can put eyes on an ALREADY-BOUND player: reopening the panel, pressing the
   *  dock's "Video tab" button, or picking the Video tab from the tab strip. A deep-link restore
   *  binds without any of them, and until one happens the poster stays unfetched — so this is the one
   *  place that says "the user has now asked to look at this", instead of each entry point deciding
   *  for itself and one of them forgetting (which is exactly what happened twice). A fresh bind from
   *  a click sets the same flag inside `bindRecording`. */
  const showPlayerByGesture = (): void => setBoundByGesture(true);

  /** The Video tab IS the player, so choosing it is one of those gestures. */
  const handleTabChange = (nextTab: string): void => {
    setTab(nextTab);
    if (nextTab === 'video') showPlayerByGesture();
  };

  /** The summary's review caption jumps to the first review case; the Cases tab must be mounted
   *  before that row can be found. */
  const showReviewCases = (): void => {
    flushSync(() => setTab('cases'));
    revealFirstReviewCase();
  };

  const onShowVideo = (): void => {
    if (boundRecording) {
      setDrawerOpen(true);
      showPlayerByGesture();
      return;
    }
    const [firstRecording] = recordings;
    if (!firstRecording) return;
    const { seekMs, caseId } = wholeRecordingSelection(
      firstRecording,
      reportCases,
    );
    handleVideoSelect(firstRecording, seekMs, caseId);
  };

  /** Restores this page's deep link (S11, AC 26/27) at most once per `runId`: waits for the report
   *  bundle (recordings/cases aren't resolvable before it lands), then binds whatever `rec`/`case`
   *  name — arming the seek rather than loading it, so nothing fetches until the user's own gesture.
   *  Reads `initialDeepLinkRef` (the props as they stood when this run's page was entered), never the
   *  live `caseId`/`rec`/`t` props — those change on every explicit bind via `syncDeepLinkUrl` above,
   *  and re-reading them here would restore a state the user just moved away from. */
  useEffect(() => {
    if (deepLinkAppliedRef.current) return;
    const link = initialDeepLinkRef.current;
    if (!link.caseId && !link.rec) {
      deepLinkAppliedRef.current = true;
      return;
    }
    if (!reportCases) return;
    deepLinkAppliedRef.current = true;

    /** Expanding the linked case and binding its recording are independent outcomes of this restore
     *  (AC 26/27) — a `case` naming a real row expands it even when no recording resolves for it (the
     *  recording was reaped, the run was re-run since the link was shared, or the URL was hand-trimmed
     *  to drop `rec`). */
    const linkedCase = link.caseId
      ? reportCases.find((candidate) => candidate.id === link.caseId)
      : undefined;
    if (linkedCase) setRestoredExpandCaseId(linkedCase.id);

    const recording = resolveDeepLinkRecording({
      recordings,
      cases: reportCases,
      caseId: link.caseId,
      recordingKey: link.rec,
      platform,
    });
    if (!recording) return;
    const expandCaseId = linkedCase?.id ?? recording.caseIds?.[0] ?? null;
    setBoundCaseId(expandCaseId);
    setBoundAttemptNumber(recording.attemptNumber ?? null);
    if (!linkedCase && expandCaseId) setRestoredExpandCaseId(expandCaseId);
    armRecording(recording, typeof link.t === 'number' ? link.t : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- armRecording/bindRecording are stable per render and reading them isn't what should re-trigger this restore
  }, [reportCases, recordings, platform]);

  const content = (
    <div
      className={cn(
        'mx-auto flex w-full flex-col gap-5 px-4 py-6 sm:px-6',
        detail?.run.run_kind === 'generation' && 'gap-3',
        /** The wider cap belongs to the split layout and nothing else: it exists so the player column
         *  is extra room beside the switcher rather than a bite out of it. Keyed on the same flag
         *  that renders the split, so an exploratory run — which keeps its own player and never
         *  splits — cannot widen into a column that will not appear. */
        'max-w-none',
        /** The floating panel sits over this scroll container's bottom-right corner, so the last rows
         *  need bottom padding matching the panel's OWN maximum footprint (`max-h-[80vh]` plus its
         *  `bottom-4` offset) while it is open. Padding less than that — an earlier 60vh guess — left
         *  the final rows of a tall portrait session unreachable without closing the player. Never on
         *  the Video tab, where the player is in flow instead. Otherwise, from `md` up, the last row
         *  still needs to scroll clear of the floating Feedback launcher in the same corner. */
        drawerOpen && boundRecording && tab !== 'video'
          ? 'pb-[calc(80vh+2rem)]'
          : 'md:pb-16',
      )}
    >
      {relaunchMode && detail && (
        <RelaunchRunDialog
          key={`${runId}-${relaunchMode}`}
          // An exploratory restart/rerun replays the saved launch snapshot server-side
          // (#resumeExploratoryRun / #rerunExploratoryRun) and accepts nothing from relaunch
          // settings besides webNetworkCaptureMode — never forward the run's project id here, or
          // the dialog offers environment/platform/HITL controls that would 400 on submit.
          projectId={
            detail.run.run_kind === 'exploratory'
              ? undefined
              : (detail.run.project_id ?? undefined)
          }
          mode={relaunchMode}
          initialEnvironmentName={detail.run.environment_name}
          initialRequestedPlatforms={detail.run.requested_platforms}
          initialHumanInteractionEnabled={
            detail.run.human_interaction
              ? detail.run.human_interaction.input_enabled ||
                detail.run.human_interaction.takeover_enabled
              : undefined
          }
          initialExecutionMode={detail.run.execution_mode}
          initialWebNetworkCaptureMode={detail.run.web_network_capture_mode}
          webNetworkCaptureAvailable={webNetworkCaptureAvailable}
          pending={restart.isPending || rerun.isPending}
          reportChanged={reportChanged}
          onClose={() => setRelaunchMode(null)}
          onLaunch={relaunchMode === 'restart' ? onRestart : onRerun}
          restartCandidates={
            relaunchMode === 'restart' ? restartCandidates : undefined
          }
        />
      )}

      <AppDialog
        open={currentRerunOpen}
        onOpenChange={(open) =>
          !rerunCurrent.isPending && setCurrentRerunOpen(open)
        }
        title="Rerun current source"
        description="Read the current provider cases and saved adaptations for the original selected cases, then start a new run."
      >
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClickCapture={(event) => {
              if (rerunCurrent.isPending) {
                event.preventDefault();
                event.stopPropagation();
                notify.warning(
                  'Wait for the current action to finish, then try again.',
                );
              }
            }}
            onClick={() => setCurrentRerunOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="brand"
            onClickCapture={(event) => {
              if (rerunCurrent.isPending || reportChanged) {
                event.preventDefault();
                event.stopPropagation();
                notify.warning(
                  'Wait for the current report revision to load and the current action to finish.',
                );
              }
            }}
            onClick={() => {
              rerunCurrent.mutate(
                { launchRequestKey: currentRerunRequestKey() },
                {
                  onSuccess: (created) => {
                    setCurrentRerunOpen(false);
                    notify.success('Current source rerun started.');
                    void navigate({
                      to: '/quality/$id',
                      params: { id: created.id },
                    });
                  },
                  onError: (error) => {
                    if (
                      error instanceof QualityApiError &&
                      error.status === 409
                    ) {
                      notify.warning(
                        'The current source changed. Refresh the run and review the source before rerunning.',
                      );
                      return;
                    }
                    notify.error(
                      error instanceof Error
                        ? error.message
                        : 'Failed to rerun the current source.',
                    );
                  },
                },
              );
            }}
          >
            {rerunCurrent.isPending ? 'Starting…' : 'Rerun current source'}
          </Button>
        </div>
      </AppDialog>

      {currentCorrectionResult ? (
        <OutcomeCorrectionDialog
          key={currentCorrectionResult.case_id}
          runId={runId}
          result={currentCorrectionResult}
          reportChanged={reportChanged}
          initialOutcome={correction?.initialOutcome}
          onClose={() => setCorrection(null)}
        />
      ) : null}

      {agentHandoff.kind === 'invalid' || handoffError ? (
        <Card>
          <CardContent className="py-4 text-sm text-warning-700">
            {agentHandoff.kind === 'invalid'
              ? agentHandoff.message
              : handoffError}
            {handoffError && agentHandoff.kind === 'valid' ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (reportQuery.isError) void reportQuery.refetch();
                  setHandoffRetry((value) => value + 1);
                }}
              >
                Retry Agent handoff
              </Button>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <Link
        to="/quality"
        // Preserve the shared project filter on the way back (omitted when the run has no project).
        search={{ tab: 'runs', projectId: detail?.run.project_id ?? undefined }}
        className="inline-flex w-fit items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeftIcon className="size-4" />
        Back to Quality
      </Link>

      {detailQuery.isLoading ? (
        <RunDetailSkeleton />
      ) : detailQuery.isError || !detail ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            This run could not be loaded. It may not exist or you may not have
            access to it.
          </CardContent>
        </Card>
      ) : (
        <>
          <Card
            data-testid="quality-run-summary-card"
            className="gap-0 overflow-hidden border-border/80 shadow-elev-2"
          >
            <CardContent
              className={
                detail.run.run_kind === 'generation'
                  ? 'px-4 py-3'
                  : 'p-5 sm:p-6'
              }
            >
              <header
                className={
                  detail.run.run_kind === 'generation'
                    ? 'flex items-start gap-3'
                    : 'flex flex-wrap items-start gap-x-4 gap-y-4'
                }
              >
                <div className="flex min-w-0 flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="font-heading text-xl font-bold tracking-tight">
                      {runName(detail.run)}
                    </h1>
                    <PlatformIcon
                      platform={detail.run.platform}
                      components={detail.run.components}
                    />
                    <CancelReasonTooltip
                      cancelledByName={detail.run.cancelled_by_name}
                    >
                      <OutcomeRunBadge
                        run={detail.run}
                        summary={detail.summary}
                      />
                    </CancelReasonTooltip>
                    <ReapedReasonBadge reason={detail.run.reaped_reason} />
                    {detail.run.history_migration?.state === 'quarantined' ||
                    detail.run.history_migration?.state ===
                      'preserved_changed' ? (
                      <Badge variant="warning">Requires review</Badge>
                    ) : null}
                    {detail.run.suite ? (
                      // The suite name links back to the suite it was launched from when the run maps to a
                      // browsable suite (TestRail run or Zephyr cycle — see suiteRef). Folder/inline/mock
                      // runs have no such page, so they stay a plain badge.
                      suiteRef ? (
                        <Link
                          to="/quality/suites/$id"
                          params={{ id: suiteRef.id }}
                          search={{
                            projectId: detail.run.project_id ?? undefined,
                            source: suiteRef.source,
                            ...(suiteRef.sourceRef
                              ? { sourceRef: suiteRef.sourceRef }
                              : {}),
                          }}
                        >
                          <Badge
                            variant="outline"
                            className="cursor-pointer gap-1 transition-colors hover:bg-muted"
                          >
                            {detail.run.suite}
                            <ExternalLinkIcon className="size-3" aria-hidden />
                          </Badge>
                        </Link>
                      ) : (
                        <Badge variant="outline">{detail.run.suite}</Badge>
                      )
                    ) : null}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {detail.run.project_name ?? '—'}
                    {detail.run.environment_name
                      ? ` · on ${detail.run.environment_name}`
                      : ''}
                    {` · started `}
                    {formatDate(detail.run.started_at)}
                    {detail.run.finished_at
                      ? ` · finished ${formatDate(detail.run.finished_at)}`
                      : ''}
                    {detail.run.created_by_name
                      ? ` · by ${detail.run.created_by_name}`
                      : ''}
                  </p>
                  {variableOverridesText(detail.run.variable_overrides) ? (
                    <p className="text-xs text-muted-foreground">
                      {variableOverridesText(detail.run.variable_overrides)}
                    </p>
                  ) : null}
                  {detail.run.history_migration?.state === 'quarantined' ||
                  detail.run.history_migration?.state ===
                    'preserved_changed' ? (
                    <p className="text-xs text-warning-700">
                      Historical data integrity review required
                      {detail.run.history_migration.reason
                        ? `: ${detail.run.history_migration.reason.replace(/_/g, ' ')}`
                        : '.'}
                    </p>
                  ) : null}
                </div>
              </header>

              {detail.run.run_kind === 'generation' &&
              projection?.kind === 'unsupported' ? (
                <div
                  className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-3 text-sm text-destructive"
                  role="alert"
                >
                  <span>{projection.message}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.location.reload()}
                  >
                    Reload page
                  </Button>
                </div>
              ) : null}
              {detail.run.run_kind !== 'generation' ? (
                <div className="mt-5 border-t border-border/70 pt-5">
                  {projection?.kind === 'canonical' ? (
                    <OutcomeResultsSummary
                      projection={projection}
                      businessExecution={
                        (detail.run.run_kind ?? 'execution') === 'execution'
                      }
                      onReviewCases={showReviewCases}
                    />
                  ) : projection?.kind === 'unsupported' ? (
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-3 text-sm text-destructive">
                      <span>{projection.message}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.location.reload()}
                      >
                        Reload page
                      </Button>
                    </div>
                  ) : (
                    <SummaryStrip
                      {...normalizeRunSummary(detail.summary, detail.results, {
                        inferCancelledResidual:
                          projection?.kind === 'legacy' &&
                          projection.status === 'cancelled',
                      })}
                      deviations={totalDeviations(detail.results)}
                    />
                  )}
                  <AnswerJudgementSummaryLine
                    summary={detail.answer_judgement_summary}
                  />
                  {reportTotals || judgeCost || executionEstimate ? (
                    <div className="mt-3 flex flex-wrap items-start gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      {reportTotals ? (
                        <span>
                          Recorded report cost:{' '}
                          <span className="font-medium text-foreground">
                            {reportTotals.total_cost_recorded === false
                              ? 'Unavailable'
                              : (formatRecordedUsd(
                                  reportTotals.total_cost_usd,
                                ) ?? 'Unavailable')}
                          </span>
                        </span>
                      ) : null}
                      {judgeCost ? (
                        <span>
                          Judge cost:{' '}
                          <span className="font-medium text-foreground">
                            {judgeCost}
                          </span>
                        </span>
                      ) : null}
                      <ExecutionEstimate estimate={executionEstimate} details />
                    </div>
                  ) : null}
                </div>
              ) : null}
            </CardContent>
          </Card>

          <GenerationResultFeedback result={detail.generation_result} />

          {detail.run.run_kind === 'generation' &&
          (lifecycleStatus === 'awaiting_review' ||
            terminal ||
            (detail.generation_result?.retained_case_count ?? 0) > 0) ? (
            <ReviewProposedCases
              runId={runId}
              run={detail.run}
              canPublishRetainedDrafts={detail.generation_result?.can_publish}
              feedbackContainer={feedbackContainer}
              onOpenAssistant={() => panel.setCollapsed(false)}
              generationNotice={retainedGenerationWarning(
                detail.generation_result,
              )}
            />
          ) : null}

          {/* Saved batches stay visible while other catalog cases remain on review. */}
          {detail.run.run_kind === 'generation' &&
          (lifecycleStatus === 'awaiting_review' || terminal) ? (
            <GeneratedCasesResult
              runId={runId}
              showGenerationMetadata={false}
              hideEmpty={lifecycleStatus === 'awaiting_review'}
              source={runOutputSource(detail.run)}
            />
          ) : null}

          {detail.run.publication_warnings?.length ? (
            <div
              role="alert"
              className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 text-sm"
            >
              <p className="font-medium">Some results were not published</p>
              {detail.run.publication_warnings.map((warning, index) => (
                <p key={`${warning.provider}-${warning.code}-${index}`}>
                  {warning.message}
                </p>
              ))}
            </div>
          ) : null}

          {projection?.kind === 'canonical' &&
          detail.run.run_kind !== 'generation' ? (
            <OutcomeActivity key={runId} runId={runId} initial={detail} />
          ) : null}

          {detail.run.queue_wait ? (
            <Card>
              <CardContent className="py-3">
                <QueueWaitNotice queueWait={detail.run.queue_wait} />
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle>Pipeline</CardTitle>
            </CardHeader>
            <CardContent>
              <StageTimeline stages={detail.stages} />
            </CardContent>
          </Card>

          {/* Human-in-the-loop: the executing agent may hit a login wall, an MFA code, a value it
              cannot fill in on its own, or a control it cannot drive at all — either calls
              request_user_input() (a value) or raises a device takeover (drive the device), and
              either pauses the run. Not gated by run_kind — any run can hit this. One block for both
              shapes (see InputRequiredPanel). Mounted even for a terminal run: the panel then reads
              no pending requests and renders only an auth flight, which stays readable after expiry
              so a terminal run can still explain the authentication failure that ended it. */}
          <InputRequiredPanel runId={runId} terminal={terminal} />

          {/* While a generation run is still working (before it has cases to review), show a live
              activity feed of what the generator is doing — the review/result panels below only
              appear once cases exist. */}
          {detail.run.run_kind === 'generation' &&
          ['running', 'reporting'].includes(lifecycleStatus ?? '') ? (
            <div
              role="status"
              className="rounded-lg border border-primary/20 bg-primary/5 p-4 text-sm"
            >
              <p className="font-medium">
                {lifecycleStatus === 'awaiting_review'
                  ? 'Your test cases are ready to review'
                  : 'Generation continues in the background'}
              </p>
              <p className="mt-1 text-muted-foreground">
                You can leave this page. This run and its draft are saved in
                Runs; open this run to continue.
              </p>
            </div>
          ) : null}

          {detail.run.run_kind === 'generation' &&
          (lifecycleStatus === 'running' || lifecycleStatus === 'reporting') ? (
            <GenerationActivity runId={runId} />
          ) : null}

          <WorkspaceKnowledgeRunNotice run={detail.run} />

          <PreflightPanel run={detail.run} />

          {/* The saved generation output (the same feed shown live while running), under the cases,
              collapsed by default — available once the run has produced cases (review or finalized). */}
          {detail.run.run_kind === 'generation' &&
          (lifecycleStatus === 'awaiting_review' || terminal) ? (
            <GenerationLog runId={runId} />
          ) : null}

          {/* Post-run Trace from the saved Claude NDJSON artifact — next to the generation log once
              review/terminal. Missing artifacts render nothing (no error spam). Live mid-run feed
              stays GenerationActivity. */}
          {detail.run.run_kind === 'generation' &&
          (lifecycleStatus === 'awaiting_review' || terminal) ? (
            <GenerationTrace
              runId={runId}
              artifactKeys={
                detail.results.find((result) => result.case_id === 'generation')
                  ?.artifact_keys ?? []
              }
            />
          ) : null}

          {/* The exploration report (execution log + potential bugs + screenshots) is for standalone
              exploratory sessions only. Generation runs produce test cases for review — nothing else. */}
          {detail.run.run_kind === 'exploratory' ? (
            <>
              {/* MEDA-934: the same live feed generation shows, kept running through an input pause
                  because the session resumes from there. Both log panels read the run row, not the
                  projection, so a run with incoherent counters still shows its log. */}
              {['running', 'awaiting_input', 'reporting'].includes(
                runLifecycleStatus(detail.run) ?? '',
              ) ? (
                <GenerationActivity runId={runId} variant="exploratory" />
              ) : null}
              <GenerationTrace
                runId={runId}
                variant="exploratory"
                environmentName={detail.run.environment_name}
                launchSnapshot={detail.run.exploratory_launch_snapshot}
                artifactKeys={explorationResult?.artifact_keys ?? []}
                terminal={terminal}
              />
              <ExplorationReport
                runId={runId}
                findings={detail.run.findings ?? []}
                executionSteps={detail.run.execution_steps ?? []}
                videoUrl={detail.run.video_url}
                incomplete={explorationReportIncomplete(detail.run)}
                incompleteReason={exploratoryIncompleteReason(
                  explorationResult,
                  detail.run.reaped_reason ?? null,
                )}
                artifactKeys={explorationResult?.artifact_keys ?? []}
              />
              {runIsOver ? (
                <GenerationLog runId={runId} variant="exploratory" />
              ) : null}
              {terminal ? (
                <ExploratoryArtifactPanel
                  runId={runId}
                  projectId={detail.run.project_id}
                />
              ) : null}
            </>
          ) : null}

          {detail.run.run_kind === 'exploration' ? (
            <>
              {['running', 'awaiting_input', 'reporting'].includes(
                runLifecycleStatus(detail.run) ?? '',
              ) ? (
                <GenerationActivity runId={runId} variant="exploratory" />
              ) : null}
              {runIsOver ? (
                <GenerationLog runId={runId} variant="exploratory" />
              ) : null}
            </>
          ) : null}

          {/* Generation runs never show the Cases/report tabs: the only result row is the internal
              synthesis bundle ("Test generation …"), not real test cases. The proposed cases live in
              the review panel (while awaiting review) and the result panel (after approval) above. */}
          {detail.run.run_kind === 'generation' ? null : (
            <Tabs
              value={tab}
              onValueChange={handleTabChange}
              className="min-w-0"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <TabsList className="flex-wrap">
                  <TabsTrigger value="cases">
                    <ListChecksIcon aria-hidden /> Cases
                  </TabsTrigger>
                  {/* Both triggers render from the first paint, disabled until their content exists.
                    Mounting them only once a recording or a digest lands made the tab list grow —
                    and on a narrow viewport wrap to a second line — in the middle of a live run. */}
                  <TabsTrigger
                    value="video"
                    onClickCapture={(event) => {
                      if (!hasVideo) {
                        event.preventDefault();
                        event.stopPropagation();
                        notify.warning(
                          'No recording is available for this run yet.',
                        );
                      }
                    }}
                  >
                    <VideoIcon aria-hidden /> Video
                  </TabsTrigger>
                  <TabsTrigger
                    value="errors"
                    onClickCapture={(event) => {
                      if (!bundle?.errors) {
                        event.preventDefault();
                        event.stopPropagation();
                        notify.warning(
                          'No error report is available for this run yet.',
                        );
                      }
                    }}
                  >
                    <BugIcon aria-hidden /> Error Digest
                  </TabsTrigger>
                </TabsList>
                <div className="ml-auto flex flex-wrap items-center gap-2">
                  {/* "Show video" (AC 7) reopens the floating panel after its close button — a dead
                    end otherwise. Pointless on the Video tab, where the player is the tab. */}
                  {hasVideo && tab !== 'video' ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onShowVideo}
                      aria-expanded={drawerOpen && Boolean(boundRecording)}
                      /** Only while the dock is actually in the DOM: with nothing bound `PlayerDock`
                       *  renders null, and pointing at an element that does not exist is worse for
                       *  assistive tech than pointing at nothing. */
                      aria-controls={
                        boundRecording ? PLAYER_DOCK_ID : undefined
                      }
                    >
                      <VideoIcon aria-hidden /> Show video
                    </Button>
                  ) : null}
                </div>
              </div>

              {/* The tab strip stays full width above this; only the tab's CONTENT shares the row with
                the player. On the Video tab that is the YouTube shape — player in the wide column,
                case switcher in the narrow one. Everywhere else it is a single column and the player
                is the floating panel instead. */}
              <div
                className={cn(
                  'grid grid-cols-1 items-start gap-6',
                  videoTabLayout &&
                    'xl:grid-cols-[minmax(0,1.85fr)_minmax(0,1fr)]',
                )}
              >
                <TabsContent value="cases" className="space-y-4">
                  <CasesReport
                    runId={runId}
                    results={detail.results}
                    cancelling={cancelCase.isPending}
                    onCancel={
                      !terminal &&
                      projection?.kind !== 'unsupported' &&
                      (detail.run.run_kind ?? 'execution') === 'execution' &&
                      (identity?.roles.includes('admin') ||
                        identity?.userId === detail.run.created_by)
                        ? (caseId) =>
                            cancelCase.mutate(caseId, {
                              onSuccess: () =>
                                notify.success('Case cancelled.'),
                              onError: (err) =>
                                notify.error(
                                  reasonOf(err, 'Failed to cancel case'),
                                ),
                            })
                        : undefined
                    }
                    reportCases={reportCases}
                    reapedReason={detail.run.reaped_reason ?? null}
                    onSeek={hasVideo ? handleSeek : undefined}
                    // Let each case row deep-link into the suite with itself opened (only when the run
                    // maps to a suite-detail page — a TestRail run or a cycle-launched Zephyr run).
                    suiteRunId={suiteRef?.id}
                    suiteProjectId={detail.run.project_id ?? undefined}
                    suiteSource={suiteRef?.source}
                    /** Failure-first auto-expansion (S10, AC 24/25) — gated on the whole run's terminal
                     *  state, not just the case's own status. */
                    terminal={terminal}
                    /** A deep link's target case (S11, AC 26/27) — expands once on restore. */
                    expandCaseId={restoredExpandCaseId ?? undefined}
                    /** "Where the step time went" (S12), rendered INSIDE the card under its header
                     *  rather than as a banner between the tab row and the table — it is a note about
                     *  the cases, not a thing to read on the way to them. */
                    note={
                      <TimeSinkStrip
                        cases={reportCases}
                        terminal={terminal}
                        onSeek={hasVideo ? handleSeek : undefined}
                      />
                    }
                    onCorrect={
                      projection?.kind === 'canonical' &&
                      terminal &&
                      bundle?.coherence === 'coherent' &&
                      bundle.reportRevision === projection.reportRevision &&
                      (detail.run.run_kind ?? 'execution') === 'execution'
                        ? (result, initialOutcome) =>
                            setCorrection({ result, initialOutcome })
                        : undefined
                    }
                  />
                  <AnswerJudgeReport
                    judgementSummary={detail.answer_judgement_summary}
                    results={detail.results}
                    runSummary={detail.summary}
                    testerModel={detail.run.model}
                  />
                </TabsContent>

                {hasVideo ? (
                  /** VideoReport (S8) is a stateless case/recording selector — no player, no blob,
                   *  nothing to preserve across a tab switch — so it mounts and unmounts with the tab
                   *  like any other panel. The shared player lives entirely in the dock below. */
                  <TabsContent value="video" className="min-w-0 xl:order-2">
                    <VideoReport
                      cases={reportCases}
                      recordings={recordings}
                      boundKey={boundRecordingKey}
                      onSelect={handleVideoSelect}
                    />
                  </TabsContent>
                ) : null}

                {bundle?.errors ? (
                  <TabsContent value="errors">
                    <ErrorDigestReport report={bundle.errors} />
                  </TabsContent>
                ) : null}
                {/* The single docked player (S5): rendered once, as a sibling of the tab PANELS — inside
              the always-mounted `<Tabs>` root, never inside a `TabsContent`, which Radix unmounts or
              hides (a hidden subtree has zero intersection, so a bearer-fetched player there would
              never load — see the plan's ADR). It sits after the panels in DOM order so keyboard
              focus reaches the report first; `order` only moves which column paints first, so the
              media element is never re-parented and never re-fetches its blob. Gated off for
              exploratory runs, which render their own player via ExplorationReport above. */}
                {detail.run.run_kind !== 'exploratory' ? (
                  <PlayerDock
                    recording={boundRecording}
                    variant={tab === 'video' ? 'inline' : 'modal'}
                    /** `xl:mt-3` matches `TabsContent`'s own top margin, so the player column and the case
                     *  switcher beside it start on the same line. */
                    className={
                      videoTabLayout
                        ? 'xl:order-1 xl:sticky xl:top-6 xl:mt-3'
                        : undefined
                    }
                    open={drawerOpen}
                    onOpenChange={setDrawerOpen}
                    onOpenVideoTab={
                      hasVideo ? () => handleTabChange('video') : undefined
                    }
                    posterUrl={boundByGesture ? boundPosterUrl : null}
                    seek={dockSeek}
                    loadRequest={dockLoadRequest}
                    caseSteps={boundCaseSteps}
                    clipWindow={boundClipWindow}
                    chapterScopeCaseId={boundCaseId}
                    chapterScopeAttemptNumber={boundAttemptNumber}
                  />
                ) : null}
                {/* The Video tab's player column while nothing is selected: it says what fills it, so the
              empty half of the tab never reads as a rendering bug. */}
                {/* Only from `xl`, where the two-column split actually exists: below it the switcher and this
              placeholder stack, so "on the right" would point at a column that isn't there — and the
              list is directly above anyway, which needs no caption. */}
                {videoTabLayout && !boundRecording ? (
                  <p className="hidden rounded-lg border border-dashed border-border p-4 text-xs text-muted-foreground xl:order-1 xl:block">
                    Pick a case on the right to watch its recording here.
                  </p>
                ) : null}
              </div>
            </Tabs>
          )}
          <ConfirmDeleteDialog
            open={confirmDelete}
            onOpenChange={setConfirmDelete}
            onConfirm={onDelete}
            name={runName(detail.run)}
            entityLabel="run"
            extraWarning="Cases already written to TestRail, Zephyr, Xray or Azure stay there."
            confirmDisabled={remove.isPending || remove.isSuccess}
          />
        </>
      )}
    </div>
  );
  const pageActions: QualityRunMenuAction[] = [];
  if (detail) {
    if (!terminal && projection?.kind !== 'unsupported')
      pageActions.push({
        id: 'cancel',
        label: 'Cancel run',
        icon: <XCircleIcon />,
        onSelect: () => guardAction(cancel.isPending, onCancel),
      });
    if (
      detail.run.run_kind !== 'validation' &&
      (terminal || lifecycleStatus === 'awaiting_input') &&
      hasFailures(detail)
    )
      pageActions.push({
        id: 'restart',
        label: 'Restart failed',
        icon: <RotateCcwIcon />,
        onSelect: () =>
          guardAction(restart.isPending || reportChanged, () =>
            webNetworkCaptureAvailable ||
            (detail.run.run_kind !== 'exploratory' &&
              (projection?.kind === 'canonical' || detail.run.project_id))
              ? openRelaunch('restart')
              : onRestart(),
          ),
      });
    if (terminal && detail.run.run_kind !== 'validation') {
      pageActions.push({
        id: 'rerun',
        label: 'Rerun again',
        icon: <RotateCwIcon />,
        onSelect: () =>
          guardAction(rerun.isPending || reportChanged, () =>
            webNetworkCaptureAvailable ||
            (detail.run.run_kind !== 'exploratory' && detail.run.project_id)
              ? openRelaunch('rerun')
              : onRerun(),
          ),
      });
      if (currentSourceRerunAvailable)
        pageActions.push({
          id: 'rerun-current',
          label: 'Rerun current source',
          icon: <RotateCwIcon />,
          onSelect: () =>
            guardAction(rerunCurrent.isPending || reportChanged, () =>
              setCurrentRerunOpen(true),
            ),
        });
      if (detail.run.run_kind === 'generation' && detail.run.project_id)
        pageActions.push({
          id: 'edit',
          label: 'Edit and run again',
          icon: <PencilIcon />,
          onSelect: () =>
            void navigate({
              to: '/quality',
              search: {
                tab: 'generate',
                projectId: detail.run.project_id ?? undefined,
                prefillRunId: runId,
              },
            }),
        });
    }
    if (terminal)
      pageActions.push({
        id: 'delete',
        label: 'Delete',
        icon: <Trash2Icon />,
        destructive: true,
        onSelect: () =>
          guardAction(remove.isPending, () => setConfirmDelete(true)),
      });
  }
  return (
    <div className="mx-auto w-full max-w-[112rem] px-2 lg:px-4">
      <AssistantLayout
        containerRef={layoutRef}
        main={content}
        panel={panel}
        fixedWidth={416}
        chat={
          detail
            ? (collapse) => (
                <Suspense
                  fallback={
                    <p
                      role="status"
                      className="p-4 text-sm text-muted-foreground"
                    >
                      Loading assistant…
                    </p>
                  }
                >
                  <QualityRunAssistant
                    key={runId}
                    detail={detail}
                    onCollapse={collapse}
                    onFeedbackContainer={setFeedbackContainer}
                    onSendReady={onSendReady}
                    actions={(send) => (
                      <>
                        <RunActions
                          key={runId}
                          menuVisible={!panel.collapsed}
                          detail={detail}
                          bundle={bundle}
                          reportUnavailable={reportChanged}
                          panelSend={send}
                          pageActions={pageActions}
                          reportActions={
                            terminal && detail.run.run_kind !== 'generation'
                          }
                        />
                        {detail.run.run_kind !== 'generation' &&
                          detail.run.run_kind !== 'exploratory' &&
                          hasFailures(detail) && (
                            <RefinePanel
                              key={runId}
                              runId={runId}
                              enabled={terminal}
                            />
                          )}
                      </>
                    )}
                  />
                </Suspense>
              )
            : null
        }
      />
    </div>
  );
}

/**
 * Whether a run has any restartable case — at least one `failed`/`broken`/`timeout`
 * result. Prefers the per-result array (mirrors the admin/original behavior);
 * falls back to the summary's failed count when results aren't populated.
 */
function hasFailures(detail: RunDetail): boolean {
  if (outcomeProjection(detail.run, detail.summary).kind === 'canonical') {
    return restartableCases(detail).length > 0;
  }
  if (detail.results.length > 0) {
    return detail.results.some(
      (r) =>
        r.status === 'failed' ||
        r.status === 'broken' ||
        r.status === 'timeout',
    );
  }
  // `summary.failed` no longer includes broken/timeout buckets — a run with only broken/timeout cases
  // is still restartable, so check all three, or the restart UX is hidden when results aren't populated.
  const s = detail.summary;
  return s.failed > 0 || (s.broken ?? 0) > 0 || (s.timeout ?? 0) > 0;
}

/**
 * Sum of every case's `deviation_count` (wave2-contract.md #2/#3) — the run-level count the strip
 * shows so a run that passed with fifteen cosmetic deviations doesn't look identical to a clean
 * one. 0 for a run whose results carry no `deviation_count` at all (a server predating wave 2),
 * and each value is coerced through `deviationCount` since the field is type-cast from the API
 * response rather than validated — an unvalidated string would otherwise concatenate into the sum.
 */
export function totalDeviations(results: TestResult[]): number {
  return results.reduce(
    (sum, result) => sum + deviationCount(result.deviation_count),
    0,
  );
}

export function exploratoryIncompleteReason(
  explorationResult:
    Pick<TestResult, 'reason_code' | 'status'> | null | undefined,
  reapedReason: Run['reaped_reason'],
): string | null {
  if (explorationResult) {
    return (
      caseReasonText(explorationResult, reapedReason ?? null) ??
      (explorationResult.status === 'queued'
        ? runReapedReasonText(reapedReason ?? null)
        : null)
    );
  }
  return runReapedReasonText(reapedReason ?? null);
}

export function explorationReportIncomplete(run: {
  status?: Run['status'];
  badge_status?: Run['badge_status'];
  outcome_protocol_version?: Run['outcome_protocol_version'];
  execution_status?: Run['execution_status'];
}): boolean {
  if (run.outcome_protocol_version === 1) {
    return (
      run.execution_status === 'partially_completed' ||
      run.execution_status === 'not_completed' ||
      run.execution_status === 'cancelled'
    );
  }
  if (run.outcome_protocol_version !== null) return false;
  return run.status
    ? badgeStatus({ status: run.status, badge_status: run.badge_status }) ===
        'incomplete' || run.status === 'cancelled'
    : false;
}

function reasonOf(err: Error, fallback: string): string {
  if (err instanceof QualityApiError) {
    if (err.status === 403)
      return "You don't have permission to manage this run.";
    return err.message;
  }
  return err.message || fallback;
}

export function SummaryStrip({
  total,
  passed,
  failed,
  broken,
  timeout,
  cancelled,
  pending,
  deviations,
}: {
  total: number;
  passed: number;
  failed: number;
  broken?: number;
  timeout?: number;
  cancelled?: number;
  pending: number;
  /** Sum of every case's `deviation_count`; 0 renders no note. */
  deviations?: number;
}): JSX.Element {
  /**
   * A fixed seven-cell set at every breakpoint. Broken/Timed out used to appear only once non-zero,
   * which grew the grid a row mid-run and shifted everything below it; they now always render and
   * simply go muted at zero.
   */
  const cells: { label: string; value: number; tone: string }[] = [
    { label: 'Total', value: total, tone: 'text-foreground' },
    { label: 'Passed', value: passed, tone: 'text-success-600' },
    { label: 'Failed', value: failed, tone: 'text-destructive' },
    { label: 'Broken', value: broken ?? 0, tone: 'text-destructive' },
    { label: 'Timed out', value: timeout ?? 0, tone: 'text-warning-600' },
    {
      label: 'Cancelled',
      value: cancelled ?? 0,
      tone: 'text-muted-foreground',
    },
    { label: 'Pending', value: pending, tone: 'text-muted-foreground' },
  ];
  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border bg-border sm:grid-cols-4 lg:grid-cols-7">
        {cells.map((cell) => (
          <div
            key={cell.label}
            className="flex flex-col items-center gap-0.5 bg-card px-3 py-4"
          >
            <span
              className={cn(
                'font-heading text-2xl font-bold tabular-nums',
                cell.value === 0 ? 'text-muted-foreground/40' : cell.tone,
              )}
            >
              {cell.value}
            </span>
            <span className="text-xs text-muted-foreground">{cell.label}</span>
          </div>
        ))}
      </div>
      {/* Additive note, not a seventh cell — a run with 0 deviations renders exactly as it did
       *  before this field existed (wave2-contract.md #2/#3). */}
      {deviations !== undefined && deviations > 0 ? (
        <p className="text-xs text-info-600">
          {deviations} step{deviations === 1 ? '' : 's'} passed despite a
          cosmetic mismatch (deviation{deviations === 1 ? '' : 's'}).
        </p>
      ) : null}
    </div>
  );
}

/** Wall-clock duration of a stage in milliseconds, or null when it can't be computed — the stage
 *  is still running, never started, carries unparseable timestamps, or spans zero time (equal or
 *  second-truncated timestamps). Null means the timeline shows no duration for that stage — better
 *  than a misleading "—" from formatDuration(0). */
function stageDurationMs(stage: RunStage): number | null {
  if (!stage.started_at || !stage.finished_at) return null;
  const ms =
    new Date(stage.finished_at).getTime() -
    new Date(stage.started_at).getTime();
  return Number.isFinite(ms) && ms > 0 ? ms : null;
}

export function StageTimeline({ stages }: { stages: RunStage[] }): JSX.Element {
  if (stages.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No stages reported yet.</p>
    );
  }
  const ordered = [...stages].sort((a, b) => a.position - b.position);
  // Narrow horizontal strip (matches the admin pipeline): each stage as an inline
  // name + status chip, wrapping to the next line as needed.
  return (
    <ul className="flex flex-wrap items-center gap-x-5 gap-y-2">
      {ordered.map((stage) => {
        const durationMs = stageDurationMs(stage);
        return (
          <li
            key={stage.name}
            className="inline-flex items-center gap-1.5 text-sm"
          >
            <span className="font-medium capitalize text-foreground">
              {stage.name.replace(/[-_]/g, ' ')}
            </span>
            <StatusBadge status={stage.status} />
            {durationMs !== null && (
              <span className="tabular-nums text-muted-foreground">
                {formatDuration(durationMs)}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** How the scarce device session behaves for this run, per the preflight gate's retention policy. */
const SESSION_LABEL: Record<'hold' | 'per-block', string> = {
  hold: 'Device session held until the run ends',
  'per-block': 'Device session released after each platform',
};

/**
 * How a legacy `preflight_error` names each case-data finding, and what that reason code means in
 * words. Only rows written before the service started spelling the explanation out carry these
 * codes; a current warning arrives already readable and passes through untouched.
 */
const LEGACY_REASON_PHRASE: Record<string, string> = {
  digit_glued_in_word: 'a number attached directly to a lowercase word',
  mojibake: 'replacement characters suggesting damaged encoding',
  control_characters: 'a hidden control character',
};

/**
 * Replaces the reason codes of a legacy case-data warning with the phrase each one means, leaving
 * the case ids beside them alone. Deliberately no more than that: the field label and step number
 * a current warning carries were never persisted on those rows, and inventing them would point the
 * reader at a field that may be clean.
 */
function humanizePreflightNotice(notice: string): string {
  return Object.entries(LEGACY_REASON_PHRASE).reduce(
    (text, [code, phrase]) => text.split(`${code}:`).join(`${phrase}:`),
    notice,
  );
}

/**
 * Whether `preflight_error` describes a gate that actually stopped the run, rather than a note the
 * run carried on past. Read from the run's terminal reason, not from the message text: the same
 * field holds case-data advisories, the pre-scan's fallback note and real credential failures, and
 * only the failures terminalize. `status` is the wrong signal — a run can fail in execution while
 * carrying an advisory from preflight, and that advisory is still only an advisory.
 */
function preflightGateFailed(run: Pick<Run, 'terminal_reason'>): boolean {
  return run.terminal_reason === 'preflight_failed';
}

/**
 * Preflight pre-scan result for a run: the components the pre-scan selected, how the scarce device
 * session behaves, and any preflight error. Renders nothing for a single-component run that cleared
 * preflight (≤1 component, no retention, no error), so those runs look exactly as before.
 */
export function PreflightPanel({
  run,
}: {
  run: Pick<
    Run,
    | 'components'
    | 'retention'
    | 'preflight_error'
    | 'attachments_incomplete'
    | 'attachment_issues'
    | 'terminal_reason'
  >;
}): JSX.Element | null {
  // `components` is cast from the API — dedupe so a repeat can't render a duplicate badge / React key.
  const components = run.components ? [...new Set(run.components)] : [];
  const hasBreakdown = components.length > 1 || Boolean(run.retention);
  if (
    !hasBreakdown &&
    !run.preflight_error &&
    !run.attachments_incomplete &&
    !run.attachment_issues?.length
  )
    return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <LayersIcon className="size-4 text-muted-foreground" aria-hidden />
          Preflight
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 text-sm">
        {components.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-muted-foreground">Components:</span>
            {components.map((component) => {
              const Icon = platformComponentIcon(component);
              return (
                <Badge
                  key={component}
                  variant="outline"
                  className="gap-1 capitalize"
                >
                  <Icon className="size-3" aria-hidden />
                  {component}
                </Badge>
              );
            })}
          </div>
        ) : null}
        {run.retention ? (
          <p className="text-xs text-foreground">
            {Object.prototype.hasOwnProperty.call(SESSION_LABEL, run.retention)
              ? SESSION_LABEL[run.retention]
              : run.retention}
          </p>
        ) : null}
        {run.preflight_error ? (
          preflightGateFailed(run) ? (
            <p className="flex items-start gap-1.5 text-xs text-destructive">
              <AlertTriangleIcon
                className="mt-0.5 size-3.5 shrink-0"
                aria-hidden
              />
              {run.preflight_error}
            </p>
          ) : (
            <p className="flex items-start gap-1.5 text-xs text-amber-600">
              <InfoIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden />
              <span>
                <span className="font-medium">
                  Advisory — this did not block the run.
                </span>{' '}
                {humanizePreflightNotice(run.preflight_error)}
              </span>
            </p>
          )
        ) : null}
        {run.attachment_issues?.length ? (
          <AttachmentIssuesNotice issues={run.attachment_issues} />
        ) : run.attachments_incomplete ? (
          <p className="flex items-start gap-1.5 text-xs text-amber-600">
            <AlertTriangleIcon
              className="mt-0.5 size-3.5 shrink-0"
              aria-hidden
            />
            Some source attachments could not be staged — affected cases may be
            missing an embedded image.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** What each attachment issue reason means for the tester, and what to do about it. */
const ATTACHMENT_ISSUE_TEXT: Record<string, string> = {
  case_attachment_limit:
    "more attachments than this project's Attachments per case limit, so these were not passed to the agent. Raise Attachments per case in the workspace's Quality settings, or split the case.",
  run_size_limit:
    "over the run's total attachment size limit, so these were not passed to the agent. Run fewer cases at once.",
  case_size_limit:
    "over the case's 250 MB attachment size limit, so these were not passed to the agent. Split the case or use smaller files.",
  file_rejected:
    'empty, larger than 25 MB, or of an unsupported file type. Replace the file in the case.',
  download_failed:
    'could not be downloaded from the test management system. Rerun to try again.',
  embedded_image_not_attached:
    'pasted into the step text but not attached to the case, so the agent cannot open them. Attach them to the case if the agent needs them.',
};

const UNKNOWN_ATTACHMENT_ISSUE_TEXT = 'could not be prepared for the agent.';

/** Group issues by case, then by reason, keeping the server's order. */
function groupAttachmentIssues(
  issues: AttachmentIssue[],
): Map<string, Map<string, string[]>> {
  const byCase = new Map<string, Map<string, string[]>>();
  for (const issue of issues) {
    const byReason = byCase.get(issue.case_id) ?? new Map<string, string[]>();
    const filenames = byReason.get(issue.reason) ?? [];
    filenames.push(issue.filename ?? 'an unnamed image');
    byReason.set(issue.reason, filenames);
    byCase.set(issue.case_id, byReason);
  }
  return byCase;
}

/** Names every source file the agent will not have, under its case, with the reason and the fix. */
function AttachmentIssuesNotice({
  issues,
}: {
  issues: AttachmentIssue[];
}): JSX.Element {
  return (
    <div className="flex items-start gap-1.5 text-xs text-amber-600">
      <AlertTriangleIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden />
      <div className="flex min-w-0 flex-col gap-1">
        <span className="font-medium">
          Some source files will not reach the agent.
        </span>
        <ul className="flex flex-col gap-1">
          {[...groupAttachmentIssues(issues)].flatMap(([caseId, byReason]) =>
            [...byReason].map(([reason, filenames]) => (
              <li key={`${caseId}:${reason}`} className="break-words">
                <span className="font-medium">{caseId}</span>:{' '}
                {filenames.join(', ')} —{' '}
                {ATTACHMENT_ISSUE_TEXT[reason] ?? UNKNOWN_ATTACHMENT_ISSUE_TEXT}
              </li>
            )),
          )}
        </ul>
      </div>
    </div>
  );
}

function RunDetailSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-3">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-5 w-16 rounded-md" />
      </div>
      <Skeleton className="h-24 w-full rounded-xl" />
      <Skeleton className="h-40 w-full rounded-xl" />
      <Skeleton className="h-64 w-full rounded-xl" />
    </div>
  );
}
